import { withStyles, WithStyles } from "@mui/styles";
import { DashboardStyleClasses } from "assets/jss/common-styles";
import { advFilterAddCommand, AdvFilterCommand, AdvFilterPicker, AdvFilterResult, AutoCompleteKeyWord } from "components/AdvFilter";
import { CmdAuthor, CmdGroup, CmdTopicNoSubs, CmdTopic, CmdUser, CommandLabels, CmdAccountBlocked } from "components/AdvFilter/CommonCommands";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPaper from "components/MuiExtras/MuiPaper";
import { WithSnackbar, withSnackbar } from "components/SnackbarProvider/Snackbar";
import HtmlUtils from "lib/HtmlUtils";
import { DashboardCourseAnalysisFilter, DashboardCourseAnalysisSummaryColumn } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RoutePaths } from "routes/RouteConfig";
import { DashboardCourseAnalysisEnrollmentsGraph } from "views/Dashboards/CourseAnalysisDashboard/CourseAnalysisEnrollments";
import CourseAnalysisFilter from "views/Dashboards/CourseAnalysisDashboard/CourseAnalysisFilter";
import { DashboardCourseAnalysisProgressGraph } from "views/Dashboards/CourseAnalysisDashboard/CourseAnalysisProgress";
import { DashboardCourseAnalysisPublishedGraph } from "views/Dashboards/CourseAnalysisDashboard/CourseAnalysisPublished";
import { DashboardCourseAnalysisSocialGraph } from "views/Dashboards/CourseAnalysisDashboard/CourseAnalysisSocial";
import CourseAnalysisTiles from "views/Dashboards/CourseAnalysisDashboard/CourseAnalysisTiles";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    activeTitle: DashboardCourseAnalysisSummaryColumn;
    filter: DashboardCourseAnalysisFilter;
    advFilters: AdvFilterResult[];
}

type Props = WithStyles<typeof DashboardStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class PublicationAnalysisDashboard extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);

        this.state = {
            activeTitle: DashboardCourseAnalysisSummaryColumn.Enrollments,
            filter: null,
            advFilters: []
        };
    }

    ///
    private getCommands(): AdvFilterCommand[] {
        const cmdLabels = CommandLabels();
        return [
            { keyword: CmdTopic, displayName: cmdLabels[CmdTopic], picker: AdvFilterPicker.Topic, group: 1, canRepeat: false, },
            { keyword: CmdTopicNoSubs, displayName: cmdLabels[CmdTopicNoSubs], group: 1, canRepeat: false, },
            { keyword: CmdAuthor, displayName: cmdLabels[CmdAuthor], picker: AdvFilterPicker.User, group: 2, canRepeat: false },
            { keyword: CmdUser, displayName: cmdLabels[CmdUser], picker: AdvFilterPicker.User, group: 3, canRepeat: false },
            { keyword: CmdGroup, displayName: cmdLabels[CmdGroup], picker: AdvFilterPicker.Group, group: 3, canRepeat: false },
            { keyword: CmdAccountBlocked, displayName: this.props.t("Views.Dashboards.CourseAnalysis.Filter.AccountBlocked"), group: 4 },
        ];
    }

    ///
    private handleSelectPublication = (publicationId: string, title: string, thumbnailUrl: string) => {
        this.setState(({ advFilters }) => {

            advFilters = advFilterAddCommand(this.getCommands(), advFilters, AutoCompleteKeyWord, { key: publicationId, label: title, thumbnailUrl: thumbnailUrl } as AutoCompleteDataModel, title);

            return { advFilters };
        }, () => this.props.snackbar.showMessage(this.props.t("Views.Dashboards.CourseAnalysis.Index.FilteringCourse", { title: title })));
    };

    ///
    private handleSelectTopic = (topicId: string, name: string, thumbnailUrl: string) => {
        this.setState(({ advFilters }) => {

            advFilters = advFilterAddCommand(this.getCommands(), advFilters, CmdTopic, topicId, name);

            return { advFilters };
        }, () => this.props.snackbar.showMessage(this.props.t("Views.Dashboards.CourseAnalysis.Index.FilteringTopic", { title: name })));
    };

    ///
    private handleSelectUser = (userId: string, name: string, thumbnailUrl: string) => {
        HtmlUtils.openUrlInNewWindow(RoutePaths.UserProfileViewer.replace(":id?", userId));
    };

    ///
    private getFilter(): DashboardCourseAnalysisFilter {
        const { advFilters } = this.state;

        var course = advFilters.find(p => p.keyword == AutoCompleteKeyWord);
        const author = advFilters.find(p => p.keyword == CmdAuthor);
        const topic = advFilters.find(p => p.keyword == CmdTopic);
        const user = advFilters.find(p => p.keyword == CmdUser);
        const group = advFilters.find(p => p.keyword == CmdGroup);
        const includeSubtopics = advFilters.find(p => p.keyword == CmdTopicNoSubs) == null;
        const includeBlockedUsers = advFilters.some(p => p.keyword == CmdAccountBlocked);

        return {
            topicId: topic == null ? null : topic.value,
            includeSubtopics: includeSubtopics,
            authorId: author == null ? null : author.value,
            courseId: course == null ? null : course.value.key,
            memberId: user?.value || group?.value || null,
            includeBlockedUsers 
        };
    }

    ///
    render() {
        const { advFilters, activeTitle } = this.state;

        var filter = this.getFilter();

        return (
            <div className={this.props.classes.dashboardLargeContainer}>

                <CourseAnalysisFilter
                    {...this.props}
                    advFilters={advFilters}
                    commands={this.getCommands()}
                    onFilterChange={(advFilters) => this.setState({ advFilters })}
                />


                <CourseAnalysisTiles
                    {...this.props}
                    activeTile={activeTitle}
                    filter={filter}
                    onChange={
                        (tile) => {
                            this.setState({ activeTitle: tile });
                        }
                    }
                />

                <MuiContainer margin="normal">
                    <MuiPaper borderVariant="accentedRound" borderColor="info">

                        {
                            activeTitle == DashboardCourseAnalysisSummaryColumn.Enrollments
                                ? <DashboardCourseAnalysisEnrollmentsGraph {...this.props} tile={activeTitle} filter={filter} onSelectPublication={this.handleSelectPublication} onSelectTopic={this.handleSelectTopic} onSelectUser={this.handleSelectUser} />
                                : activeTitle == DashboardCourseAnalysisSummaryColumn.Progress
                                    ? <DashboardCourseAnalysisProgressGraph {...this.props} tile={activeTitle} filter={filter} onSelectPublication={this.handleSelectPublication} onSelectTopic={this.handleSelectTopic} onSelectUser={this.handleSelectUser} />
                                    : activeTitle == DashboardCourseAnalysisSummaryColumn.Likes || activeTitle == DashboardCourseAnalysisSummaryColumn.Comments
                                        ? <DashboardCourseAnalysisSocialGraph {...this.props} tile={activeTitle} filter={filter} />
                                        : activeTitle == DashboardCourseAnalysisSummaryColumn.Published
                                            ? <DashboardCourseAnalysisPublishedGraph {...this.props} tile={activeTitle} filter={filter} />
                                            : null
                        }

                    </MuiPaper>
                </MuiContainer>

            </div>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(DashboardStyleClasses)(PublicationAnalysisDashboard)));